// Bandeja de casos · HU-34 (MG-ILE-67), spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-67.md`.
//
// Nació como la bandeja del registrador (T-37, Sprint 1: retomar un borrador o abrir el
// expediente) y en el Sprint 3 es la de TODOS los roles del proceso, cada uno dentro de su
// ámbito (RN-60). Lee `GET /tableros/bandeja` (AT S3 §2.2.8).
//
// LO QUE ESTA PANTALLA NO HACE, Y ES LA MITAD DEL DISEÑO (`estandares_frontend.md` §8.1):
//   · no ordena: el servidor entrega la página ya ordenada ANTES de paginar, por urgencia
//     —vencido, por vencer, en plazo, sin plazo configurado, cumplido— (HU-34-C1) o por la
//     cabecera que se pulsó (`orden` y `direccion`, operador, 5-oct-2026);
//   · no calcula plazos ni tiempos: el plazo llega con su texto corto, su detalle, su código y
//     su ícono, con el reloj que vence antes ya elegido (C3, C4, C7, C8); el tiempo en la etapa
//     llega en horas hábiles y en texto;
//   · no recorta por ámbito: un país fuera del ámbito es un 403 con el texto del contrato
//     (C5), y se muestra tal cual;
//   · no sabe nada de la sección de personas: la fila lleva un ícono «Afecta a personas» junto
//     al número y nada más (RN-42, C6).
//
// Retomar un borrador sigue siendo EL camino de retomar (decisión del operador, 28-ago-2026):
// `verCaso` -> `desdeCaso` -> espejo local -> `onRetomar(casoId, guardado)`.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Clock, FileEdit, FilePlus2, FilterX, Inbox, RefreshCw, ShieldOff, UserRound } from 'lucide-react';
import {
  Alert, Button, Card, Codigo, DataTable, EmptyState, Field, Input, Mono, PageHeader, SUPERFICIE, Select, StatusBadge,
  VistaToggle,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import Bandera from '../components/Banderas';
import SeguimientoDelCaso from '../components/SeguimientoDelCaso';
import TextoDelPlazo from '../components/TextoDelPlazo';
import { consultarBandejaGeneral } from '../api/tableros';
import type {
  BandejaGeneral, CasoEnBandeja, DireccionDelOrden, FiltrosDeLaBandejaGeneral, OrdenDeLaBandeja,
} from '../api/tableros';
import { verCaso } from '../api/casos';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { desdeCaso } from '../lib/mapeo';
import { conPersonasDelEspejo, guardarEspejo, recordarEnCurso } from '../lib/borradorLocal';
import { comoFechaDeInstante, comoFechaYHora } from '../lib/fechas';
import { filtrosIniciales, guardarFiltros } from '../lib/filtrosPersistidos';
import { useCatalogosOpcional, useZonaDelPais } from '../lib/CatalogosContexto';
import { useMaestroDelPais } from '../lib/useMaestroDelPais';
import RangoDeFechas from '../components/ui/RangoDeFechas';
import SelectorDePais from '../components/ui/SelectorDePais';
import FiltrosColapsables from '../components/ui/FiltrosColapsables';
import CabeceraOrdenable from '../components/ui/CabeceraOrdenable';
import type { SentidoDelOrden } from '../components/ui/CabeceraOrdenable';
import {
  ESTADOS_DEL_CASO, GRAVEDADES, etiquetaDeLaGravedad, etiquetaDelEstadoDelCaso, tonoDeLaGravedad, tonoDelEstadoDelCaso,
} from '../lib/etiquetas';

/** 10 filas por página, paginadas por el servidor (`ui/MG-ILE-67.md`, «Estados»; operador, 5-oct-2026). */
const POR_PAGINA = 10;

/** El número completo de un caso: `PE-001-2026`. */
const NUMERO_DE_CASO = /^[A-Z]{2}-\d{3,}-\d{4}$/;
const TEXTO_NUMERO_INVALIDO = 'Escribe el número completo, por ejemplo PE-001-2026.';

type Filtros = Record<keyof FiltrosDeLaBandejaGeneral, string>;

const SIN_FILTROS: Filtros = {
  pais: '', producto_sku: '', estado: '', gravedad: '', numero: '',
  recepcion_desde: '', recepcion_hasta: '', registro_desde: '', registro_hasta: '',
  presentacion_desde: '', presentacion_hasta: '',
};

/**
 * Las tres fechas por las que se filtra (HU-34-C2 y C10), UNA A LA VEZ: el filtro «Fecha» elige
 * cuál y un solo `RangoDeFechas` pone el rango (decisión del operador, 5-oct-2026: los seis
 * campos de antes, en una rejilla aparte, se veían sobrecargados y desalineados). Al servidor
 * viajan los mismos `*_desde`/`*_hasta` de siempre, solo los del tipo elegido.
 */
const RANGOS = [
  { clave: 'recepcion', etiqueta: 'Recepción', desde: 'recepcion_desde', hasta: 'recepcion_hasta' },
  { clave: 'registro', etiqueta: 'Registro', desde: 'registro_desde', hasta: 'registro_hasta' },
  { clave: 'presentacion', etiqueta: 'Presentación', desde: 'presentacion_desde', hasta: 'presentacion_hasta' },
] as const;
type Rango = (typeof RANGOS)[number];
type TipoDeFecha = Rango['clave'];
const rangoDe = (tipo: TipoDeFecha): Rango => RANGOS.find((r) => r.clave === tipo) ?? RANGOS[0];

/** El tipo con rango puesto, o Recepción (el día cero del plazo) si no hay ninguno. */
const tipoConRango = (f: Filtros): TipoDeFecha => RANGOS.find((r) => f[r.desde] || f[r.hasta])?.clave ?? 'recepcion';

/**
 * Filtros guardados con la pantalla anterior pueden traer VARIOS rangos a la vez: se conserva el
 * primero y se olvidan los demás, para que lo que se filtra sea lo que la pantalla muestra.
 */
const conUnSoloRango = (f: Filtros): Filtros => {
  const tipo = tipoConRango(f);
  return RANGOS.reduce<Filtros>((acc, r) => (r.clave === tipo ? acc : { ...acc, [r.desde]: '', [r.hasta]: '' }), f);
};

const rangoInvertido = (f: Filtros, r: Rango): boolean =>
  Boolean(f[r.desde] && f[r.hasta] && f[r.hasta] < f[r.desde]);

const hayFiltros = (f: Filtros): boolean => Object.values(f).some(Boolean);

/**
 * Cuántos filtros hay puestos, para la cabecera plegada de «Filtros»: cada campo con valor cuenta
 * uno y un rango de fechas cuenta UNO aunque tenga desde y hasta. El tipo de fecha solo no cuenta:
 * sin rango no filtra nada.
 */
const filtrosActivos = (f: Filtros): number =>
  [f.pais, f.producto_sku, f.estado, f.gravedad, f.numero].filter(Boolean).length
  + RANGOS.filter((r) => f[r.desde] || f[r.hasta]).length;

/** Solo lo puesto: el filtro «todos» es no mandar la clave. */
const aConsulta = (f: Filtros): FiltrosDeLaBandejaGeneral =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v)) as FiltrosDeLaBandejaGeneral;

/**
 * Cómo se lee un fallo de la consulta (`ui/MG-ILE-67.md`, «Estados»): un 403 o un 422 con un
 * filtro puesto es el filtro el que se rechaza (fuera del ámbito, HU-34-C5) —error de envío,
 * la lista anterior sigue—; un 403 sin filtro es que esta bandeja no es para el rol; lo demás
 * es un error de carga, con «Reintentar».
 */
type ClaseDeFallo = 'envio' | 'permiso' | 'carga';
const claseDeFallo = (fallo: Fallo, conFiltros: boolean): ClaseDeFallo => {
  if ((fallo.estado === 403 || fallo.estado === 422) && conFiltros) return 'envio';
  if (fallo.estado === 403) return 'permiso';
  return 'carga';
};

/** RN-82: el día de un instante del caso, en la zona del país del caso. */
function FechaDelCaso({ caso, valor }: { caso: CasoEnBandeja; valor: string | null | undefined }) {
  const zona = useZonaDelPais()(caso.pais);
  return <>{comoFechaDeInstante(valor, zona)}</>;
}

/**
 * ORDEN POR CABECERA (operador, 5-oct-2026). Lo aplica el SERVIDOR antes de paginar: la pantalla
 * solo dice por qué columna y en qué dirección. Primer clic ascendente, segundo descendente,
 * tercero vuelve a «urgencia» (`null`: no se manda la clave).
 */
type ColumnaOrdenable = Exclude<OrdenDeLaBandeja, 'urgencia'>;
type Orden = { columna: ColumnaOrdenable; direccion: DireccionDelOrden } | null;

const ORDENABLES: readonly ColumnaOrdenable[] = [
  'numero', 'pais', 'producto', 'etapa', 'gravedad', 'plazo', 'responsable', 'en_la_etapa', 'recibido',
];
const esOrdenable = (valor: string): valor is ColumnaOrdenable => (ORDENABLES as readonly string[]).includes(valor);

/**
 * El orden guardado con los filtros (`lib/filtrosPersistidos.ts`, misma clave `general`). Lo que
 * no sea una columna y una dirección conocidas se ignora: de aquí sale la query string.
 */
const ordenInicial = (): Orden => {
  const { orden, direccion } = filtrosIniciales('general', { orden: '', direccion: '' });
  return esOrdenable(orden) && (direccion === 'asc' || direccion === 'desc') ? { columna: orden, direccion } : null;
};

const siguienteOrden = (actual: Orden, columna: ColumnaOrdenable): Orden => {
  if (actual?.columna !== columna) return { columna, direccion: 'asc' };
  return actual.direccion === 'asc' ? { columna, direccion: 'desc' } : null;
};

/** Las columnas secundarias se ocultan en el móvil; la vista de tarjetas las muestra todas. */
const OCULTA_EN_MOVIL = 'max-md:hidden';

type DefinicionDeColumna = {
  key: string;
  titulo: string;
  /** El testid de la celda (`<td>`), el mismo en todas las filas. */
  testid?: string;
  orden?: ColumnaOrdenable;
  className?: string;
};

const COLUMNAS: DefinicionDeColumna[] = [
  { key: 'numero', titulo: 'N.º de caso', testid: 'bandeja-fila-numero', orden: 'numero' },
  { key: 'pais', titulo: 'País', testid: 'bandeja-fila-pais', orden: 'pais' },
  { key: 'producto', titulo: 'Producto', testid: 'bandeja-fila-producto', orden: 'producto', className: OCULTA_EN_MOVIL },
  // La etapa es el estado del caso con su texto del servidor: el testid sigue siendo el de siempre.
  { key: 'etapa', titulo: 'Etapa', testid: 'bandeja-fila-estado', orden: 'etapa' },
  { key: 'gravedad', titulo: 'Gravedad', testid: 'bandeja-fila-gravedad', orden: 'gravedad' },
  { key: 'plazo', titulo: 'Plazo', testid: 'bandeja-fila-plazo', orden: 'plazo', className: 'min-w-[10rem]' },
  { key: 'responsable', titulo: 'Responsable', testid: 'bandeja-fila-responsable', orden: 'responsable', className: OCULTA_EN_MOVIL },
  { key: 'en_la_etapa', titulo: 'En la etapa', testid: 'bandeja-fila-en-la-etapa', orden: 'en_la_etapa' },
  // Recibido solo desde 1536 px: a 1440 la fila no cabía. El dato sigue en el modal de seguimiento.
  { key: 'recibido', titulo: 'Recibido', testid: 'bandeja-fila-recibido', orden: 'recibido', className: 'max-2xl:hidden' },
  { key: 'seguimiento', titulo: 'Seguimiento', className: 'w-px' },
];

const tituloDe = (columna: ColumnaOrdenable): string => COLUMNAS.find((c) => c.orden === columna)?.titulo ?? columna;

/** `bandeja-orden-en-la-etapa`: el testid de cada cabecera ordenable, en kebab-case (§7). */
const testidDelOrden = (columna: ColumnaOrdenable): string => 'bandeja-orden-' + columna.replaceAll('_', '-');
const ATRIBUTO_DE_PRUEBA = 'data-testid';

const sentidoDe = (orden: Orden, columna: ColumnaOrdenable): SentidoDelOrden => {
  if (orden?.columna !== columna) return 'none';
  return orden.direccion === 'asc' ? 'ascending' : 'descending';
};

/** Las cabeceras ordenables llevan su botón (catálogo) y el `aria-sort` en el `th`. */
const columnasDeLaTabla = (orden: Orden, onOrdenar: (columna: ColumnaOrdenable) => void): ColumnaDeTabla[] =>
  COLUMNAS.map((c) => {
    const columna = c.orden;
    // «Seguimiento» es una columna de ícono: su nombre queda para el lector de pantalla.
    if (!columna) return { key: c.key, testid: c.testid, className: c.className, label: <span className="sr-only">{c.titulo}</span> };
    const sentido = sentidoDe(orden, columna);
    return {
      key: c.key,
      testid: c.testid,
      className: c.className,
      ariaSort: sentido,
      label: (
        <CabeceraOrdenable data-testid={testidDelOrden(columna)} sentido={sentido} onClick={() => onOrdenar(columna)}>
          {c.titulo}
        </CabeceraOrdenable>
      ),
    };
  });

/**
 * HU-34-C3: el plazo con su ícono y su texto, nunca solo el color. Arriba el texto corto en
 * negrita («Vencido hace 2 días»), debajo el detalle («Vencía 03/10/2026 · notificación»). En
 * «Sin plazo» y «No aplica» el detalle es la ayuda emergente (y texto para el lector de pantalla).
 * Todo lo da el servidor; con un servidor anterior, sin texto corto, se pinta el semáforo.
 * La celda la comparte la alerta de plazos del Dashboard (`components/TextoDelPlazo.tsx`, HU-34b V1).
 */
function PlazoDelCaso({ caso }: { caso: CasoEnBandeja }) {
  // CC-02 (B10, AT S3 §11.3.8): con más de un destino, debajo los destinos tal como los escribe
  // el servidor («SENASA · SANIPES»). Con uno, la celda es la de siempre.
  const destinos = (caso.destinos?.length ?? 0) > 1 && caso.texto_de_los_destinos && (
    <span className="text-meta text-ink-soft" data-testid="bandeja-fila-destinos">{caso.texto_de_los_destinos}</span>
  );
  return (
    <TextoDelPlazo plazo={{
      estado: caso.semaforo.estado,
      icono: caso.icono_del_semaforo,
      textoDelSemaforo: caso.texto_del_semaforo,
      textoCorto: caso.texto_corto_del_plazo,
      detalle: caso.detalle_del_plazo,
      fiable: caso.plazo_fiable,
    }}>
      {destinos}
    </TextoDelPlazo>
  );
}

function PaisDelCaso({ caso }: { caso: CasoEnBandeja }) {
  return (
    <span className="inline-flex items-center gap-2 text-compacto whitespace-nowrap">
      <Bandera cod={caso.pais} nombre={caso.nombre_pais ?? caso.pais} />
      {caso.nombre_pais ?? caso.pais}
    </span>
  );
}

/**
 * HU-34-C6 · RN-42: de la sección de personas, la fila solo dice que el caso las afecta. Es un
 * ícono junto al número (operador, 5-oct-2026: se quitó la columna «Personas») con nombre
 * accesible; lleva el testid que la columna tenía.
 */
function IconoDePersonas({ caso }: { caso: CasoEnBandeja }) {
  if (!caso.afecta_a_personas) return null;
  return (
    <span role="img" aria-label="Afecta a personas" title="Afecta a personas" data-testid="bandeja-fila-personas"
      className="inline-grid place-items-center w-5 h-5 rounded-full bg-rose-50 text-rose-800 shrink-0">
      <UserRound size={12} strokeWidth={2.4} aria-hidden="true" />
    </span>
  );
}

type Acciones = {
  retomando: string | null;
  onRetomar?: (caso: CasoEnBandeja) => void;
  onAbrir?: (casoId: string) => void;
  /** Abre el seguimiento del caso; el botón es adonde vuelve el foco al cerrarlo. */
  onSeguimiento?: (caso: CasoEnBandeja, boton: HTMLElement) => void;
};

/**
 * El número del caso ES el enlace que lo abre (spec: «N.º de caso · `Mono` con enlace»). Un caso
 * enviado se ABRE en el expediente (`bandeja-fila-abrir`), que es de solo lectura para quien no
 * actúa sobre él: lo decide el servidor al abrirlo.
 *
 * Un borrador no tiene número (RN-01) y solo lo ve quien lo registra (HU-34-C9): se RETOMA
 * (`bandeja-fila-retomar`, spec `MG-ILE-370.md`) si quien mira puede registrar.
 */
function NumeroDelCaso({ caso, acciones }: { caso: CasoEnBandeja; acciones: Acciones }) {
  const { retomando, onRetomar, onAbrir } = acciones;
  if (caso.estado === 'borrador' || !caso.numero) {
    const abriendo = retomando === caso.caso_id;
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-compacto text-ink-soft">Borrador</span>
        <IconoDePersonas caso={caso} />
        {onRetomar && (
          <Button size="sm" variant="secondary" icon={FileEdit} disabled={abriendo}
            data-testid="bandeja-fila-retomar" aria-label="Retomar el borrador" onClick={() => onRetomar(caso)}>
            {abriendo ? 'Abriendo…' : 'Retomar'}
          </Button>
        )}
      </span>
    );
  }
  const numero = <Codigo className="whitespace-nowrap text-ink">{caso.numero}</Codigo>;
  return (
    <span className="inline-flex items-center gap-1.5">
      {onAbrir ? (
        <Button size="sm" variant="ghost" data-testid="bandeja-fila-abrir" aria-label={`Abrir ${caso.numero}`}
          onClick={() => onAbrir(caso.caso_id)}>
          {numero}
        </Button>
      ) : numero}
      <IconoDePersonas caso={caso} />
    </span>
  );
}

/**
 * Cuánto lleva el caso en su etapa (horas hábiles con el horario de oficina) y desde cuándo; lo
 * calcula el servidor. Sin horario configurado las horas llegan nulas: «Sin horario», neutro,
 * con la ayuda de qué falta. Cerrado no está en ninguna etapa que vigilar.
 */
function EnLaEtapa({ caso }: { caso: CasoEnBandeja }) {
  if (caso.estado === 'cerrado') return <span className="text-compacto text-ink-soft">Cerrado</span>;
  // Un servidor anterior no trae el tiempo en la etapa: la celda queda vacía, no inventada.
  if (caso.texto_en_la_etapa === undefined && !caso.en_la_etapa_desde) return null;
  const sinHorario = caso.horas_habiles_en_la_etapa === null || caso.horas_habiles_en_la_etapa === undefined;
  return (
    <span className="flex flex-col items-start gap-0.5">
      {sinHorario ? (
        <span title="Falta configurar el horario de oficina" data-testid="bandeja-fila-sin-horario">
          <StatusBadge tone="neutral" dot={false}>{caso.texto_en_la_etapa || 'Sin horario'}</StatusBadge>
          <span className="sr-only">. Falta configurar el horario de oficina</span>
        </span>
      ) : (
        <b className="font-semibold text-ink whitespace-nowrap"><Mono>{caso.texto_en_la_etapa}</Mono></b>
      )}
      {caso.en_la_etapa_desde && (
        <span className="text-meta text-ink-soft whitespace-nowrap">desde <Mono><FechaDelCaso caso={caso} valor={caso.en_la_etapa_desde} /></Mono></span>
      )}
    </span>
  );
}

/** «Seguimiento»: abre el modal del flujo del caso. Un borrador no tiene flujo que seguir. */
function BotonDeSeguimiento({ caso, acciones }: { caso: CasoEnBandeja; acciones: Acciones }) {
  if (!caso.numero || !acciones.onSeguimiento) return null;
  const abrir = acciones.onSeguimiento;
  return (
    // Solo el ícono (a 1440 px la fila no cabía con el texto): el nombre accesible y la ayuda
    // emergente dicen qué hace.
    <Button size="sm" variant="ghost" icon={Clock} data-testid="bandeja-fila-seguimiento"
      aria-label={`Ver el seguimiento de ${caso.numero}`} title="Ver el seguimiento del caso"
      onClick={(e) => abrir(caso, e.currentTarget)} />
  );
}

/** Una celda, la misma en la tabla y en la tarjeta: un hecho se pinta en un solo sitio. */
function celdaDelCaso(caso: CasoEnBandeja, clave: string, acciones: Acciones): ReactNode {
  switch (clave) {
    case 'numero': return <NumeroDelCaso caso={caso} acciones={acciones} />;
    case 'pais': return <PaisDelCaso caso={caso} />;
    case 'producto': return <span className="text-compacto">{caso.producto_principal ?? ''}</span>;
    // ui/MG-ILE-67 · la etapa y la gravedad con el texto del servidor (§8.1 R1); el glosario
    // queda solo para el tono y para un servidor anterior, que no trae el texto.
    case 'etapa':
      return (
        <span className="whitespace-nowrap">
          <StatusBadge tone={tonoDelEstadoDelCaso(caso.estado)}>{caso.texto_del_estado || etiquetaDelEstadoDelCaso(caso.estado)}</StatusBadge>
        </span>
      );
    case 'gravedad':
      return (
        <span className="whitespace-nowrap">
          <StatusBadge tone={tonoDeLaGravedad(caso.gravedad)}>{caso.texto_de_la_gravedad || etiquetaDeLaGravedad(caso.gravedad)}</StatusBadge>
        </span>
      );
    case 'plazo': return <PlazoDelCaso caso={caso} />;
    case 'responsable':
      if (caso.responsable_actual === null) return <span className="text-compacto text-ink-soft">Nadie (cerrado)</span>;
      return <span className="text-compacto">{caso.responsable_actual ?? ''}</span>;
    case 'en_la_etapa': return <EnLaEtapa caso={caso} />;
    case 'recibido': return <Mono className="whitespace-nowrap text-compacto"><FechaDelCaso caso={caso} valor={caso.fecha_recepcion} /></Mono>;
    case 'seguimiento': return <BotonDeSeguimiento caso={caso} acciones={acciones} />;
    default: return null;
  }
}

/** La vista de tarjetas (`VistaToggle`): los mismos hechos y los mismos testids que la fila. */
function TarjetasDeCasos({ casos, acciones }: { casos: CasoEnBandeja[]; acciones: Acciones }) {
  const campos = COLUMNAS.filter((c) => !['numero', 'plazo', 'seguimiento'].includes(c.key));
  return (
    <ul data-testid="bandeja-exito" aria-label="Casos" className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
      {casos.map((caso) => (
        <li key={caso.caso_id} data-testid="bandeja-fila" className={SUPERFICIE + ' p-4 flex flex-col gap-3'}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <span data-testid="bandeja-fila-numero">{celdaDelCaso(caso, 'numero', acciones)}</span>
            <span data-testid="bandeja-fila-plazo">{celdaDelCaso(caso, 'plazo', acciones)}</span>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 items-center">
            {campos.map((c) => (
              <div key={c.key} className="contents">
                <dt className="text-meta font-semibold text-ink-soft">{c.titulo}</dt>
                <dd data-testid={c.testid}>{celdaDelCaso(caso, c.key, acciones)}</dd>
              </div>
            ))}
          </dl>
          <div className="flex justify-end">{celdaDelCaso(caso, 'seguimiento', acciones)}</div>
        </li>
      ))}
    </ul>
  );
}

/** Lo que va dentro de la tarjeta de resultados: cargando, sin permiso, vacío o la lista. */
function ContenidoDeLaBandeja({ cargando, sinPermiso, bandeja, conFiltros, vista, orden, acciones, onOrdenar, onQuitarFiltros, onNuevo }: {
  cargando: boolean;
  sinPermiso: Fallo | null;
  bandeja: BandejaGeneral | null;
  conFiltros: boolean;
  vista: string;
  orden: Orden;
  acciones: Acciones;
  onOrdenar: (columna: ColumnaOrdenable) => void;
  onQuitarFiltros: () => void;
  onNuevo?: () => void;
}) {
  if (cargando) {
    return <p data-testid="bandeja-cargando" role="status" className="p-6 text-body text-ink-soft">Cargando los casos…</p>;
  }
  if (sinPermiso) {
    return (
      <EmptyState data-testid="bandeja-sin-permiso" icon={ShieldOff} title="No tienes acceso a la bandeja de casos"
        description={sinPermiso.message} />
    );
  }
  if (!bandeja) return null;
  if (!bandeja.casos.length) {
    return (
      <EmptyState
        data-testid="bandeja-vacio"
        icon={Inbox}
        title={conFiltros ? 'Ningún caso cumple esos filtros' : 'Todavía no hay casos'}
        description={conFiltros
          ? 'Prueba con otros filtros o quítalos.'
          : 'Cuando se registre una sospecha de evento adverso aparecerá aquí.'}
        action={conFiltros
          ? <Button variant="secondary" icon={FilterX} onClick={onQuitarFiltros}>Quitar filtros</Button>
          : onNuevo && <Button variant="secondary" icon={FilePlus2} onClick={onNuevo}>Registrar un caso</Button>}
      />
    );
  }
  if (vista === 'tarjetas') return <TarjetasDeCasos casos={bandeja.casos} acciones={acciones} />;
  const filas: FilaDeTabla[] = bandeja.casos.map((c) => ({ ...c, id: c.caso_id }));
  return (
    <DataTable
      data-testid="bandeja-exito"
      testidDeFila="bandeja-fila"
      columns={columnasDeLaTabla(orden, onOrdenar)}
      rows={filas}
      renderCell={(fila, columna) => celdaDelCaso(fila as unknown as CasoEnBandeja, columna.key, acciones)}
      sinMarco
    />
  );
}

/** «Mostrando 1 a 10 de 1 000 casos», el orden pedido y la paginación del servidor. */
function PieDeLaLista({ bandeja, pagina, orden, onPagina }: {
  bandeja: BandejaGeneral; pagina: number; orden: Orden; onPagina: (pagina: number) => void;
}) {
  const paginas = Math.max(1, Math.ceil(bandeja.total / bandeja.por_pagina));
  const primero = bandeja.casos.length ? (pagina - 1) * bandeja.por_pagina + 1 : 0;
  const ultimo = (pagina - 1) * bandeja.por_pagina + bandeja.casos.length;
  return (
    <>
      <p className="text-meta text-ink-soft" role="status">
        Mostrando <Mono className="text-ink">{primero ? `${primero} a ${ultimo}` : '0'}</Mono> de{' '}
        <Mono className="text-ink">{bandeja.total}</Mono> casos.
        {orden && (
          <span data-testid="bandeja-ordenado-por">
            {' '}Ordenado por <span className="text-ink font-medium">{tituloDe(orden.columna)}</span>,{' '}
            {orden.direccion === 'asc' ? 'ascendente' : 'descendente'}.
          </span>
        )}
        {/* ui/MG-ILE-67 · «Refrescar»: cuándo armó el servidor esta lista. */}
        {bandeja.consultada_en && (
          <span data-testid="bandeja-consultada-en">
            {' '}Consultada el <Mono className="text-ink">{/* RN-82: la consulta no es de ningún caso ni país (la bandeja mezcla países): Lima. */}{comoFechaYHora(bandeja.consultada_en, null)}</Mono>
          </span>
        )}
      </p>
      {paginas > 1 && (
        <nav className="flex items-center gap-2" aria-label="Paginación de la bandeja">
          <Button size="sm" variant="secondary" disabled={pagina <= 1} onClick={() => onPagina(pagina - 1)}>
            ← Anterior
          </Button>
          <span className="text-meta text-ink-soft whitespace-nowrap">
            Página <Mono className="text-ink">{pagina}</Mono> de <Mono className="text-ink">{paginas}</Mono>
          </span>
          <Button size="sm" variant="secondary" disabled={pagina >= paginas} onClick={() => onPagina(pagina + 1)}>
            Siguiente →
          </Button>
        </nav>
      )}
    </>
  );
}

export default function Bandeja({ onRetomar, onVerExpediente, onNuevo }: {
  onRetomar?: (casoId: string, guardado: unknown) => void;
  onVerExpediente?: (casoId: string) => void;
  onNuevo?: () => void;
}) {
  const catalogos = useCatalogosOpcional();
  // RN-82: el borrador que se retoma se traduce con la zona de SU país (recepción e inicio del evento).
  const zonaDe = useZonaDelPais();
  // Los filtros sobreviven a abrir un caso y volver (`lib/filtrosPersistidos.ts`).
  const [filtros, setFiltros] = useState<Filtros>(() => conUnSoloRango(filtrosIniciales('general', SIN_FILTROS)));
  const [tipoDeFecha, setTipoDeFecha] = useState<TipoDeFecha>(() => tipoConRango(filtros));
  const [numeroEscrito, setNumeroEscrito] = useState(filtros.numero);
  const [errorDelNumero, setErrorDelNumero] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [recarga, setRecarga] = useState(0);
  const [vista, setVista] = useState('lista');
  const [bandeja, setBandeja] = useState<BandejaGeneral | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<{ fallo: Fallo; clase: ClaseDeFallo } | null>(null);
  const [falloAlRetomar, setFalloAlRetomar] = useState<Fallo | null>(null);
  // El borrador que se está trayendo: su botón se bloquea contra el doble clic.
  const [retomando, setRetomando] = useState<string | null>(null);
  // El orden por cabecera: `null` es «urgencia», el del servidor por defecto (HU-34-C1).
  const [orden, setOrden] = useState<Orden>(ordenInicial);
  // El caso cuyo seguimiento está abierto y el botón adonde vuelve el foco al cerrarlo.
  const [seguimiento, setSeguimiento] = useState<{ casoId: string; numero: string | null; pais: string } | null>(null);
  const volverAlBoton = useRef<HTMLElement | null>(null);
  const enCurso = useRef<AbortController | null>(null);

  const conFiltros = hayFiltros(filtros);
  const algunRangoInvertido = RANGOS.some((r) => rangoInvertido(filtros, r));

  // El orden se guarda con los filtros (una sola entrada `general`): vuelve igual tras abrir un caso.
  useEffect(() => {
    guardarFiltros('general', { ...filtros, ...(orden ? { orden: orden.columna, direccion: orden.direccion } : {}) });
  }, [filtros, orden]);

  // Mientras el servidor reordena la tabla se cambia por «Cargando»: al volver, el foco regresa a
  // la cabecera pulsada para que el teclado no lo pierda (WCAG 2.4.3).
  const ordenPulsado = useRef<ColumnaOrdenable | null>(null);
  useEffect(() => {
    if (cargando || !ordenPulsado.current) return;
    document.querySelector<HTMLElement>(`[${ATRIBUTO_DE_PRUEBA}="${testidDelOrden(ordenPulsado.current)}"]`)?.focus();
    ordenPulsado.current = null;
  }, [cargando]);

  // Al cerrar el seguimiento, el foco vuelve al botón que lo abrió (WCAG 2.4.3).
  useEffect(() => {
    if (seguimiento || !volverAlBoton.current) return;
    volverAlBoton.current.focus();
    volverAlBoton.current = null;
  }, [seguimiento]);

  const cargar = useCallback(async () => {
    enCurso.current?.abort();
    const control = new AbortController();
    enCurso.current = control;
    setCargando(true);
    setFallo(null);
    try {
      const respuesta = await consultarBandejaGeneral(aConsulta(filtros), {
        pagina, porPagina: POR_PAGINA, orden: orden?.columna, direccion: orden?.direccion, senal: control.signal,
      });
      if (control.signal.aborted) return;
      setBandeja(respuesta);
    } catch (error) {
      if (control.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      const normalizado = aFallo(error);
      setFallo({ fallo: normalizado, clase: claseDeFallo(normalizado, hayFiltros(filtros)) });
    } finally {
      if (!control.signal.aborted) setCargando(false);
    }
  }, [filtros, pagina, orden]);

  useEffect(() => {
    // Un rango invertido no se manda: el error está bajo el campo y la lista anterior sigue.
    // Una consulta nueva cancela la anterior dentro de `cargar`: la respuesta vieja no pisa la
    // tabla del filtro recién elegido.
    if (!algunRangoInvertido) cargar();
  }, [cargar, algunRangoInvertido, recarga]);
  useEffect(() => () => enCurso.current?.abort(), []);

  const cambiar = (cambio: Partial<Filtros>) => {
    // Otro país es otro maestro (HU-29): el producto elegido puede no existir en él.
    setFiltros((f) => ({ ...f, ...('pais' in cambio && cambio.pais !== f.pais ? { producto_sku: '' } : {}), ...cambio }));
    setPagina(1);
  };

  // Otro tipo de fecha conserva el rango puesto: se mueve a las claves del tipo nuevo.
  const cambiarTipoDeFecha = (tipo: TipoDeFecha) => {
    const antes = rangoDe(tipoDeFecha);
    const despues = rangoDe(tipo);
    setTipoDeFecha(tipo);
    if (antes === despues || (!filtros[antes.desde] && !filtros[antes.hasta])) return;
    cambiar({ [antes.desde]: '', [antes.hasta]: '', [despues.desde]: filtros[antes.desde], [despues.hasta]: filtros[antes.hasta] });
  };
  const rango = rangoDe(tipoDeFecha);

  const quitarFiltros = () => {
    setFiltros(SIN_FILTROS);
    setTipoDeFecha('recepcion');
    setNumeroEscrito('');
    setErrorDelNumero(null);
    setPagina(1);
  };

  /** Buscar por número: se aplica con Enter, en cualquier estado del ámbito. */
  const buscar = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const numero = numeroEscrito.trim().toUpperCase();
    if (numero && !NUMERO_DE_CASO.test(numero)) {
      setErrorDelNumero(TEXTO_NUMERO_INVALIDO);
      return;
    }
    setErrorDelNumero(null);
    setNumeroEscrito(numero);
    cambiar({ numero });
  };

  /**
   * Retomar un borrador DESDE EL SERVIDOR: `GET /casos/{id}` traducido con `desdeCaso`, con la
   * sección 7 del espejo de esta pestaña si la había (L2, el GET no la sirve). Es la misma
   * función que usa la recarga (BUG-370): un solo camino para rehidratar.
   */
  const retomar = async (caso: CasoEnBandeja) => {
    setFalloAlRetomar(null);
    setRetomando(caso.caso_id);
    try {
      const delApi = await verCaso(caso.caso_id);
      const formulario = conPersonasDelEspejo(caso.caso_id, desdeCaso(delApi, zonaDe(delApi.pais_evento)));
      guardarEspejo(caso.caso_id, formulario, delApi.version_bloqueo);
      recordarEnCurso(caso.caso_id);
      onRetomar?.(caso.caso_id, { versionBloqueo: delApi.version_bloqueo, caso: formulario });
    } catch (error) {
      setFalloAlRetomar(aFallo(error));
    } finally {
      setRetomando(null);
    }
  };

  /** Otro orden lo aplica el servidor sobre TODA la lista: se vuelve a la página 1. */
  const ordenar = (columna: ColumnaOrdenable) => {
    ordenPulsado.current = columna;
    setOrden((actual) => siguienteOrden(actual, columna));
    setPagina(1);
  };

  const abrirSeguimiento = (caso: CasoEnBandeja, boton: HTMLElement) => {
    volverAlBoton.current = boton;
    setSeguimiento({ casoId: caso.caso_id, numero: caso.numero, pais: caso.pais });
  };

  const acciones: Acciones = {
    // `retomar` es asíncrona y atrapa sus propios fallos: la acción de la fila no espera nada.
    retomando, onRetomar: onRetomar ? (caso) => { void retomar(caso); } : undefined,
    onAbrir: onVerExpediente, onSeguimiento: abrirSeguimiento,
  };
  const paises = catalogos?.PAISES ?? [];
  // El filtro de producto ofrece el maestro DEL PAÍS FILTRADO (spec, «Producto»; HU-29, tarea
  // 29-6): desde el bloque J el maestro es por país (`producto_pais`) y ya no vive en los
  // catálogos de la aplicación. Sin país elegido no hay maestro que ofrecer.
  const maestro = useMaestroDelPais(filtros.pais || null);
  const productos = maestro.productos;
  const sinPermiso = fallo?.clase === 'permiso' ? fallo.fallo : null;

  return (
    <>
      <PageHeader
        eyebrow="Casos · Bandeja"
        hu="HU-34 · MG-ILE-67"
        title="Bandeja de casos"
        subtitle="Los casos de tu ámbito, del más urgente al menos urgente según su plazo."
        icon={Inbox}
        actions={
          <>
            {onNuevo && (
              <Button variant="primary" size="sm" icon={FilePlus2} onClick={onNuevo} data-testid="bandeja-nuevo">
                Nuevo caso
              </Button>
            )}
            <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => setRecarga((n) => n + 1)}
              disabled={cargando} data-testid="bandeja-refrescar">
              Refrescar
            </Button>
          </>
        }
      />

      {/* Plegable y abierta al entrar (operador, 5-oct-2026); plegada dice cuántos hay puestos. */}
      <FiltrosColapsables className="mb-4" data-testid="bandeja-filtros" activos={filtrosActivos(filtros)}
        actions={(
          <Button variant="ghost" size="sm" icon={FilterX} onClick={quitarFiltros} disabled={!conFiltros && !numeroEscrito}
            data-testid="bandeja-quitar-filtros">
            Quitar filtros
          </Button>
        )}>
        {/* UNA rejilla para todos los filtros, en tantas columnas de 15 rem como quepan (cuatro a
            1366: en cinco se cortaban «Elige primero el país» y «Todas las gravedades», revision del
            2-oct-2026). La fecha va en la misma rejilla y alineada (operador, 5-oct-2026): el tipo
            en una columna y el rango en dos desde 1024 px, donde caben. */}
        <div className="grid gap-x-4 gap-y-3 grid-cols-[repeat(auto-fill,minmax(min(100%,15rem),1fr))]">
          <Field label="País">
            {/* Con la bandera de cada país, como todo selector de país (operador, 5-oct-2026). */}
            <SelectorDePais data-testid="bandeja-filtro-pais" value={filtros.pais} placeholder="Todos los países"
              onChange={(e) => cambiar({ pais: String(e.target.value ?? '') })}
              paises={paises} />
          </Field>
          <Field label="Producto">
            <Select data-testid="bandeja-filtro-producto" buscable value={filtros.producto_sku}
              disabled={!filtros.pais}
              placeholder={filtros.pais ? 'Todos los productos' : 'Elige primero el país'}
              onChange={(e) => cambiar({ producto_sku: String(e.target.value ?? '') })}
              options={productos.map((p) => ({ value: p.sku, label: p.nombre, hint: p.sku }))} />
          </Field>
          <Field label="Estado">
            <Select data-testid="bandeja-filtro-estado" value={filtros.estado} placeholder="Todos los estados"
              onChange={(e) => cambiar({ estado: String(e.target.value ?? '') })}
              options={Object.entries(ESTADOS_DEL_CASO).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="Gravedad">
            <Select data-testid="bandeja-filtro-gravedad" value={filtros.gravedad} placeholder="Todas las gravedades"
              onChange={(e) => cambiar({ gravedad: String(e.target.value ?? '') })}
              options={Object.entries(GRAVEDADES).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="Buscar por número" error={errorDelNumero ?? undefined}>
            <Input data-testid="bandeja-buscar" value={numeroEscrito} placeholder="PE-001-2026" enterKeyHint="search"
              onChange={(e) => { setNumeroEscrito(e.target.value); setErrorDelNumero(null); }}
              onKeyDown={buscar} />
          </Field>
          <Field label="Fecha">
            <Select data-testid="bandeja-filtro-fecha-tipo" value={tipoDeFecha} sinVacio
              onChange={(e) => cambiarTipoDeFecha(String(e.target.value) as TipoDeFecha)}
              options={RANGOS.map((r) => ({ value: r.clave, label: r.etiqueta }))} />
          </Field>
          <Field label="Rango" className="lg:col-span-2">
            <RangoDeFechas data-testid="bandeja-filtro-fecha" desde={filtros[rango.desde]} hasta={filtros[rango.hasta]}
              onCambio={({ desde, hasta }) => cambiar({ [rango.desde]: desde, [rango.hasta]: hasta })} />
          </Field>
        </div>
      </FiltrosColapsables>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        {bandeja && !cargando && !sinPermiso && <PieDeLaLista bandeja={bandeja} pagina={pagina} orden={orden} onPagina={setPagina} />}
        <span className="ml-auto"><VistaToggle vista={vista} onChange={setVista} /></span>
      </div>

      {fallo?.clase === 'envio' && (
        <div className="mb-4">
          <Alert data-testid="bandeja-error-envio" tone="danger" title="No se pudo aplicar el filtro"
            detalleTecnico={fallo.fallo.detalleTecnico}>
            {fallo.fallo.message}
          </Alert>
        </div>
      )}
      {fallo?.clase === 'carga' && (
        <div className="mb-4">
          <Alert data-testid="bandeja-error" tone="danger" title="No se pudo cargar la bandeja"
            detalleTecnico={fallo.fallo.detalleTecnico}>
            {fallo.fallo.message}
            {fallo.fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
            <span className="block mt-2">
              <Button size="sm" variant="secondary" icon={RefreshCw} onClick={() => setRecarga((n) => n + 1)}>Reintentar</Button>
            </span>
          </Alert>
        </div>
      )}
      {falloAlRetomar && (
        <div className="mb-4">
          <Alert tone="danger" title="No se pudo retomar el borrador" detalleTecnico={falloAlRetomar.detalleTecnico}>
            {falloAlRetomar.message}
          </Alert>
        </div>
      )}

      <Card pad={false}>
        <ContenidoDeLaBandeja
          cargando={cargando}
          sinPermiso={sinPermiso}
          bandeja={fallo?.clase === 'carga' ? null : bandeja}
          conFiltros={conFiltros}
          vista={vista}
          orden={orden}
          acciones={acciones}
          onOrdenar={ordenar}
          onQuitarFiltros={quitarFiltros}
          onNuevo={onNuevo}
        />
      </Card>

      {/* El modal del seguimiento del caso (operador, 5-oct-2026): se monta solo con un caso elegido. */}
      {seguimiento && (
        <SeguimientoDelCaso casoId={seguimiento.casoId} numero={seguimiento.numero} pais={seguimiento.pais} onCerrar={() => setSeguimiento(null)} />
      )}
    </>
  );
}
